import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { getCurrentAccount } from 'dashboard/helper/permissionsHelper';

/**
 * Acesso do usuário ATUAL ao Assistente Vision, na conta atual.
 *
 * Regra de produto (2026-09-13): por padrão só o ADMINISTRADOR tem o Vision,
 * com todas as tools. Agente só tem Vision se um administrador liberar tools
 * no papel personalizado dele — não existe mais "papel sem restrição":
 *
 *   administrador                     → tudo liberado (allowedTools null)
 *   agente sem papel personalizado    → Vision desligado
 *   papel com vision_tools null / []  → Vision desligado
 *   papel com vision_tools [...]      → só estas tools
 *
 * Quem manda é o backend: `AccountUser#vision_tools_allowlist` calcula a mesma
 * coisa no servidor, o `VisionController` devolve 403 e a allowlist que vai pro
 * orchestrator sai de lá. Isto aqui só evita oferecer um botão que daria 403.
 *
 * O papel (`role`) e o `custom_role` vêm do MESMO objeto de conta em
 * `getCurrentUser.accounts[]` (mesma fonte do getter `getCurrentRole`), então
 * admin e allowlist nunca são lidos de contas diferentes.
 *
 * @returns {{ visionAllowed: import('vue').ComputedRef<boolean>,
 *             allowedTools: import('vue').ComputedRef<string[]|null> }}
 */
export function useVisionAccess() {
  const currentUser = useMapGetter('getCurrentUser');
  const currentAccountId = useMapGetter('getCurrentAccountId');

  const currentAccount = computed(() => {
    // `getCurrentAccount` faz `accounts.find` direto: usuário ainda não
    // carregado (getter devolve {}) quebraria aqui.
    if (!Array.isArray(currentUser.value?.accounts)) return null;

    return getCurrentAccount(currentUser.value, currentAccountId.value) || null;
  });

  const isAdministrator = computed(
    () => currentAccount.value?.role === 'administrator'
  );

  const allowedTools = computed(() => {
    if (isAdministrator.value) return null;

    const tools = currentAccount.value?.custom_role?.vision_tools;
    // Sem papel personalizado, ou papel sem lista, é o mesmo que lista vazia:
    // nenhuma tool liberada.
    return Array.isArray(tools) ? tools : [];
  });

  const visionAllowed = computed(
    () => isAdministrator.value || allowedTools.value.length > 0
  );

  return { visionAllowed, allowedTools };
}

export default useVisionAccess;
